import { IconType, type InputTypeProps } from "@common/declarations";
import classNames from "classnames";
import * as React from "react";
import Select from "../Select/component";
import TextInput from "../Text/component";
import { COUNTRIES, COUNTRY_OPTIONS, formatNumber, guessCountry } from "./lib";

import "./style.less";

export type PhoneInputProps = InputTypeProps<string> & {
  inputClassName?: string;
  isRequired?: boolean;
  maxLength?: number;
  label?: any;
  onBlur?: () => void;
  onFocus?: () => void;

  style?: React.CSSProperties;
  tabIndex?: number;
};

const PhoneInput = React.forwardRef(
  (
    { className, inputClassName, label, onChange, style, tabIndex, value, ...props }: PhoneInputProps,
    ref: React.RefObject<HTMLDivElement>
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);

    const _onChange = React.useCallback(
      (value: string) => {
        const country = guessCountry(value);
        onChange(formatNumber(value, country).replace(/[\s\(\)]/g, ""));
      },
      [onChange]
    );

    const _onCountryChange = React.useCallback((value: string) => {
      const country = COUNTRIES.find((x) => x.id === value)!;
      onChange(country.dialCode);
    }, []);

    const onBlur = React.useCallback(() => {
      setIsFocused(false);
      props.onBlur && props.onBlur();
    }, [props.onBlur]);

    const onFocus = React.useCallback(() => {
      setIsFocused(true);
      props.onFocus && props.onFocus();
    }, [props.onFocus]);

    const country = guessCountry(value);

    return (
      <div
        ref={ref}
        className={classNames("phone_input", className, {
          focused: isFocused,
          empty: !value
        })}
        style={style}
        tabIndex={tabIndex}
      >
        <Select
          icon={country?.id ? undefined : IconType.Globe}
          iconSize={16}
          blankOptionLabel=""
          dropdownClassName="country_select"
          selectedLabel={(x) => x.prepend}
          onBlur={onBlur}
          onChange={_onCountryChange}
          onFocus={onFocus}
          options={COUNTRY_OPTIONS}
          value={country?.id}
        />

        <TextInput
          {...props}
          autoComplete="tel"
          className={inputClassName}
          onBlur={onBlur}
          onChange={_onChange}
          onFocus={onFocus}
          maxLength={64}
          value={formatNumber(value, country)}
        />
        {label}
      </div>
    );
  }
);

export default PhoneInput;
